import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import get from 'lodash/get';
import set from 'lodash/set';
import valid from 'card-validator';
import { format } from 'date-fns';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { togglePaymentProcessing } from '../../../actions/payments';
import * as api from '../../../actions/api';
import * as router from '../../../constants/router';
import * as regex from '../../../constants/regex';
import isSupportedCard from '../../../utils/isSupportedCard';
import invoiceDataSelector from '../../../selectors/invoice';
import validatePayment from '../../../utils/validatePayment';
import validateForm from '../../../utils/validateForm';
import paymentsSelector from '../../../selectors/payments';
import PaymentInput from '../PaymentInput';
import useStyles from '../../../utils/useStyles';
import { InvoiceContext } from '../../context/invoiceData';
import { useSurchargeCalculation } from '../../../hooks/usePaymentSubmit';

const CreditCardPayment = (props) => {
  const {
    invoiceID,
    currentPaymentAmount,
    setCurrentPaymentAmount,
  } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectorData = useSelector(invoiceDataSelector);
  const { submitting } = useSelector(paymentsSelector);

  const {
    paymentAgreementCC: paymentAgreement,
    allowPartialPayment,
    allowOverPayment,
    invoiceItems,
    dueDate,
    amountDue,
    maxPaymentAmount,
    minPaymentAmount,
    remainingBalance,
    showCVC,
    requireCVC,
    showBillingStreetAddress,
    requireBillingStreetAddress,
    showBillingZip,
    requireBillingZip,
    surchargeFee,
    surchargeRate,
    surchargeLabel,
    surchargeMin,
    surchargeMax,
    surchargeAddition,
    contactFirstName,
    contactLastName,
    pastPayments,
    createdDate,
  } = selectorData;

  const invoiceContext = useContext(InvoiceContext);
  const styles = useStyles();
  const [cardType, setCardType] = useState(null);
  const [acceptedAgreement, toggleAcceptedAgreement] = useState(false);
  const [cardLength, setCardLength] = useState('');

  const {
    getSubtotal,
    getTransactionWithSurcharge,
    getSurchargeAmount,
  } = useSurchargeCalculation({
    allowPartialPayment,
    amountDue,
    surchargeMin,
    surchargeMax,
    productTransactionKey: 'cc_product_transaction',
  });

  const formik = useFormik({
    initialValues: {
      cardNumber: '',
      expiryDate: null,
      cvc: '',
      nameOnCard: '',
      billingStreetAddress: '',
      billingZip: '',
      paymentAmount: '',
      subtotal_amount: '',
      surcharge_amount: '',
    },
    validate: (values) => {
      const errors = {};
      const paymentAmountFloat = parseFloat(values.paymentAmount);
      const remainingBalanceFloat = parseFloat(remainingBalance);

      const formFields = [
        {
          name: 'cardNumber',
          isRequired: true,
          value: values.cardNumber.toString().trim(),
        },
        {
          name: 'expiryDate',
          isRequired: true,
          value: values.expiryDate,
        },
        {
          name: 'cvc',
          isRequired: requireCVC,
          value: values.cvc,
        },
        {
          name: 'nameOnCard',
          isRequired: true,
          value: values.nameOnCard.trim(),
          error: 'Name is required.',
        },
        {
          name: 'billingStreetAddress',
          isRequired: requireBillingStreetAddress,
          value: values.billingStreetAddress.trim(),
          error: 'Street address is required.',
        },
        {
          name: 'billingZip',
          isRequired: requireBillingZip,
          value: values.billingZip.trim(),
        },
      ];

      for (let i = 0; i < formFields.length; i += 1) {
        const error = validateForm(formFields[i]);
        if (error !== false) {
          set(errors, formFields[i].name, error);
        }
      }

      const paymentInfo = {
        amount: paymentAmountFloat,
        remaining: remainingBalanceFloat,
        max: maxPaymentAmount,
        min: minPaymentAmount,
        over: allowOverPayment,
        partial: allowPartialPayment,
      };
      const paymentValidationError = validatePayment(paymentInfo);
      if (paymentValidationError !== false) {
        set(errors, 'paymentAmount', paymentValidationError);
      }

      return errors;
    },
    onSubmit: (values) => {
      dispatch(togglePaymentProcessing(true));

      const transactionRequest = [];
      const payload = {
        account_holder_name: values.nameOnCard,
        account_number: values.cardNumber,
        billing_street: values.billingStreetAddress,
        billing_zip: values.billingZip,
        cvv: values.cvc,
        exp_date: format(new Date(values.expiryDate), 'MMyy'),
        payment_method: 'cc',
        terms_agree: '1',
        transaction_amount: getTransactionWithSurcharge(values.paymentAmount),
        subtotal_amount: getSubtotal(values.paymentAmount),
        surcharge_amount: getSurchargeAmount(values.paymentAmount),
      };
      transactionRequest.push(payload);
      api.submitPayment(invoiceID, payload).then((res) => {
        dispatch(togglePaymentProcessing(false));
        let errors = get(res, 'data.errors', null);
        const status = get(res, 'status', 0);
        const transactionInfo = get(res, 'data.quickinvoice.transactions', [])[0];
        const statusId = get(transactionInfo, 'status_id');
        if (errors === null && status === 201 && statusId === 301) {
          errors = { declined: 'Payment Declined' };
        }
        if (errors === null && status === 201 && statusId !== 301) {
          navigate(router.PAYMENT_STATUS_PAGE, {
            state: {
              invoiceNumber: get(res, 'data.quickinvoice.invoice_number'),
              invoiceTitle: get(res, 'data.quickinvoice.title', ''),
              remainingBalance: get(res, 'data.quickinvoice.remaining_balance', ''),
              dueDate: get(res, 'data.quickinvoice.due_date', null),
              amountDue: get(res, 'data.quickinvoice.amount_due'),
              surchargeFee,
              surchargeRate,
              surchargeAddition,
              surchargeMin,
              surchargeMax,
              surchargeLabel,
              transactionInfo,
              transactionRequest,
              contactName: values.nameOnCard || '',
              lastFour: values.cardNumber.slice(values.cardNumber.length - 4),
              cardType,
              success: true,
            },
          });
        } else {
          navigate(router.PAYMENT_STATUS_PAGE, {
            state: {
              invoiceNumber: get(res, 'data.quickinvoice.invoice_number'),
              invoiceTitle: get(res, 'data.quickinvoice.title', ''),
              transactionInfo: get(res, 'data.quickinvoice.transactions', [])[0],
              productTransaction: get(res, 'data.quickinvoice.cc_product_transaction_id'),
              invoiceItems,
              contactName: values.nameOnCard || '',
              lastFour: values.cardNumber.slice(values.cardNumber.length - 4),
              cardType,
              transactionRequest,
              pastPayments,
              createdDate,
              dueDate,
              amountDue,
              surchargeFee,
              surchargeRate,
              surchargeMin,
              surchargeMax,
              surchargeAddition,
              surchargeLabel,
              remainingBalance,
              success: false,
              errors,
            },
          });
        }
      }).catch(() => {
        dispatch(togglePaymentProcessing(false));
      });
    },
    validateOnBlur: true,
    validateOnChange: true,
  });

  useEffect(() => {
    invoiceContext.setACHFlag(false);
    if (contactFirstName && contactLastName) {
      formik.setFieldValue('nameOnCard', `${contactFirstName} ${contactLastName}`);
    }
  }, [contactFirstName, contactLastName]);

  useEffect(() => {
    formik.setFieldValue('paymentAmount', currentPaymentAmount);
  }, [currentPaymentAmount]);

  return (
    <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
      <div className="cc-row-main">
        <div>
          <TextField
            variant="standard"
            sx={styles.formLabel}
            required
            id="card-number"
            className="input-cc-number"
            name="cardNumber"
            label="Card Number"
            value={formik.values.cardNumber}
            onBlur={formik.handleBlur}
            onChange={(e) => {
              // eslint-disable-next-line no-restricted-globals
              if (e.target.value.trim() !== '' && isNaN(e.target.value)) {
                return;
              }
              const cardNumber = valid.number(e.target.value.trim());
              setCardType(get(cardNumber, 'card.type'));
              const cardDigits = e.target.value.trim();
              if (cardDigits.length === 16) {
                setCardLength(cardDigits.length);
                invoiceContext.getSurchargeStatus(cardDigits);
              }
              formik.handleChange(e);
            }}
            inputProps={{ maxLength: 19 }}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {isSupportedCard(cardType) && cardType ? <img className="cc-img" src={`${process.env.PUBLIC_URL}/img/${cardType}.png`} alt="logo" /> : null}
                  {!isSupportedCard(cardType) || !cardType ? <CreditCardIcon className="credit-card-icon" /> : null}
                </InputAdornment>),
            }}
          />
          {(formik.errors.cardNumber && formik.touched.cardNumber) ? <div className="input-error">{formik.errors.cardNumber}</div> : null}
        </div>
        <div className="month-cvc-wrapper">
          <div className="input-cc-expiry-date">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                sx={styles.formLabel}
                label="MM/YY"
                format="MM/yy"
                views={['month', 'year']}
                name="expiryDate"
                minDate={new Date()}
                value={formik.values.expiryDate}
                onChange={(dateObject) => {
                  formik.setFieldValue('expiryDate', dateObject);
                }}
                slotProps={{
                  textField: {
                    variant: 'standard',
                    required: true,
                    onBlur: formik.handleBlur,
                    name: 'expiryDate',
                  },
                }}
              />
            </LocalizationProvider>
            {(formik.errors.expiryDate && formik.touched.expiryDate) ? <div className="input-error">{formik.errors.expiryDate}</div> : null}
          </div>
          {showCVC && (
            <div className="input-cc-cvc">
              <TextField
                variant="standard"
                sx={styles.formLabel}
                required={requireCVC}
                id="cvc"
                name="cvc"
                label="CVC"
                value={formik.values.cvc}
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  // eslint-disable-next-line no-restricted-globals
                  if (e.target.value.trim() !== '' && isNaN(e.target.value, 10)) {
                    return;
                  }
                  formik.handleChange(e);
                }}
                inputProps={{ maxLength: 4 }}
              />
              {(formik.errors.cvc && formik.touched.cvc) ? <div className="input-error">{formik.errors.cvc}</div> : null}
            </div>
          )}
        </div>
      </div>
      <div className="cc-second-row">
        <TextField
          variant="standard"
          sx={styles.formLabel}
          required
          className="input-cc-name-on-card"
          id="name-on-card"
          name="nameOnCard"
          label="Name on Card"
          value={formik.values.nameOnCard}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          inputProps={{ maxLength: 60 }}
        />
        {(formik.errors.nameOnCard && formik.touched.nameOnCard) ? <div className="input-error">{formik.errors.nameOnCard}</div> : null}
      </div>
      <div className="cc-row">
        {showBillingStreetAddress && (
          <div>
            <TextField
              variant="standard"
              sx={styles.formLabel}
              required={requireBillingStreetAddress}
              className="input-cc-billing-address"
              id="billing-street-address"
              name="billingStreetAddress"
              label="Billing Street Address"
              value={formik.values.billingStreetAddress}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
            {(formik.errors.billingStreetAddress && formik.touched.billingStreetAddress) ? <div className="input-error">{formik.errors.billingStreetAddress}</div> : null}
          </div>
        )}
        {showBillingZip && (
          <div className="cc-billing-zip">
            <TextField
              variant="standard"
              sx={styles.formLabel}
              required={requireBillingZip}
              id="billing-zip"
              name="billingZip"
              label="Billing Zip"
              value={formik.values.billingZip}
              onBlur={formik.handleBlur}
              onChange={(e) => {
                const input = e.target.value.trim();
                if (input !== '' && regex.alphaNumericSpaceDash.test(input) === false) {
                  return;
                }
                formik.handleChange(e);
              }}
              inputProps={{ maxLength: 12 }}
            />
            {(formik.errors.billingZip && formik.touched.billingZip) ? <div className="input-error">{formik.errors.billingZip}</div> : null}
          </div>
        )}
      </div>
      <PaymentInput
        agreement={paymentAgreement}
        submitting={submitting}
        acceptedAgreement={acceptedAgreement}
        allowPartialPayment={allowPartialPayment}
        allowOverPayment={allowOverPayment}
        remainingBalance={remainingBalance}
        paymentAmount={formik.values.paymentAmount}
        paymentAmountError={formik.errors.paymentAmount}
        paymentAmountTouched={formik.touched.paymentAmount}
        invoiceID={invoiceID}
        toggleAcceptedAgreement={() => toggleAcceptedAgreement(!acceptedAgreement)}
        handleChange={(e) => {
          formik.handleChange(e);
          setCurrentPaymentAmount(e.target.value);
        }}
        handleBlur={(e) => formik.handleBlur(e)}
        surchargeFlag={invoiceContext.surcharge.isSurcharge}
        cardLength={cardLength}
      />
    </form>
  );
};

CreditCardPayment.propTypes = {
  invoiceID: PropTypes.string.isRequired,
  currentPaymentAmount: PropTypes.string.isRequired,
  setCurrentPaymentAmount: PropTypes.func.isRequired,
};

export default CreditCardPayment;
